import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { AT_LEAST_ONE_ALPHABET, NOT_ALLOWED_SPECIAL_CHARS } from '../../../shared/utils/constants';
import { ApiService } from '../../../shared/services/api.service';
import { ExecutionSource, GetMetadataQuery, QueryTemplate, QueryWithExecutionRecord, QueryType } from '../../../../API';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { MetadataColumnsType, Metadata } from '../../../../API';
import { NzMessageService } from 'ng-zorro-antd/message';
import { FanExplorationService } from '../../services/fan-exploration.service';
import {
  DataBaseTableItem,
  SegmentFilterNode,
  SegmentSelectedFilterConfig,
} from '../../models/market-fan-exploration.model';
import { SQL_INJECTION_PATTERN } from '../../../shared/utils/constants';

@Component({
  selector: 'app-segment-size-analyzer',
  templateUrl: './segment-size-analyzer.component.html',
  styleUrl: './segment-size-analyzer.component.scss',
})
export class SegmentSizeAnalyzerComponent implements OnInit, OnChanges {
  @Input() editQueryRecord: QueryWithExecutionRecord | null = null;
  @Output() historyTabRequested = new EventEmitter<void>();

  segmentForm: FormGroup;
  data: unknown;
  isMetadataLoading = false;
  metadataErrorMessage = '';
  dataSourceList: string[] = [];
  tableList: DataBaseTableItem[] = [];
  databaseList: string[] = [];
  selectedDatasources = new FormControl<string[]>([], {
    nonNullable: true,
    validators: [Validators.required],
  });
  selectedDatabases = new FormControl<string[]>([], {
    nonNullable: true,
    validators: [Validators.required],
  });
  tableMap = new Map<string, DataBaseTableItem[]>();
  dataSourceToDatabasesMap = new Map<string, string[]>();
  previousDatasourcesValue: string[] = [];

  generatedQueryText = '';
  templates: QueryTemplate[] = [];
  isButtonDisabled = true;
  isSubmittingQuery = false;
  isSavingDraft = false;
  queryId = '';
  executionId = '';
  columnList: MetadataColumnsType[] = [];

  readonly noMetadataMessage = 'No schema metadata available for this organization.';
  private readonly listOperators = new Set(['in', 'not_in']);
  private readonly numericOperators = new Set([
    'less_than',
    'greater_than',
    'less_than_or_equal',
    'greater_than_or_equal',
  ]);
  private metadataLoaded = false;

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private userManagementService: UserManagementService,
    private messageService: NzMessageService,
    private fanExplorationService: FanExplorationService,
  ) {
    this.segmentForm = this.fb.group({
      segmentName: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(50),
          Validators.pattern(AT_LEAST_ONE_ALPHABET),
          Validators.pattern(NOT_ALLOWED_SPECIAL_CHARS),
        ],
      ],
      segmentDesc: ['', [Validators.required, Validators.maxLength(100), Validators.pattern(AT_LEAST_ONE_ALPHABET)]],
      dataSource: ['', Validators.required],
      database: ['', Validators.required],
      baseTable: ['', Validators.required],
      operator: ['AND'],
      singleRows: this.fb.array([]),
      groups: this.fb.array([]),
    });
  }
  ngOnInit(): void {
    this.addSingleRow();
    this.subscribeToMetadataSelectionChanges();
    void this.loadMetadata().then(() => {
      this.metadataLoaded = true;
      this.applyEditQueryRecord();
    });

    this.segmentForm.statusChanges.subscribe(() => {
      this.updateGenerateButtonState();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editQueryRecord'] && this.metadataLoaded) {
      this.applyEditQueryRecord();
    }
  }

  get generatedQuery(): string {
    return this.generatedQueryText;
  }

  readonly operatorsOptions = [
    { name: '==', type: 'string', value: 'equal_to' },
    { name: '!=', type: 'string', value: 'not_equal_to' },
    { name: 'like', type: 'string', value: 'like' },
    { name: '<', type: 'int', value: 'less_than' },
    { name: '>', type: 'int', value: 'greater_than' },
    { name: '<=', type: 'int', value: 'less_than_or_equal' },
    { name: '>=', type: 'int', value: 'greater_than_or_equal' },
    { name: 'in', type: 'string', value: 'in' },
    { name: 'not in', type: 'string', value: 'not_in' },
  ];

  readonly booleanOperatorOptions = [
    { label: 'AND', value: 'AND' },
    { label: 'OR', value: 'OR' },
  ];

  createRow(): FormGroup {
    const row = this.fb.group({
      column: ['', Validators.required],
      op: ['', Validators.required],
      value: ['', [Validators.required, this.createConditionValueValidator()]],
      operator: [''], // true = AND, false = OR
    });

    row.get('column')?.addValidators(this.createConditionColumnValidator());
    this.bindConditionRowValidation(row);

    return row;
  }

  createGroup(): FormGroup {
    return this.fb.group({
      rows: this.fb.array([this.createRow()]),
      groups: this.fb.array([]),
      operator: [''],
    });
  }

  get singleRows(): FormArray {
    return this.segmentForm.get('singleRows') as FormArray;
  }

  get groups(): FormArray {
    return this.segmentForm.get('groups') as FormArray;
  }

  addSingleRow(): void {
    this.singleRows.push(this.createRow());
  }

  addGroup(): void {
    this.groups.push(this.createGroup());
  }

  getRows(group: AbstractControl): FormArray {
    return (group as FormGroup).get('rows') as FormArray;
  }

  getGroups(group: AbstractControl): FormArray {
    return (group as FormGroup).get('groups') as FormArray;
  }

  addRowToGroup(group: FormGroup): void {
    this.getRows(group).push(this.createRow());
  }

  addNestedGroup(group: FormGroup): void {
    this.getGroups(group).push(this.createGroup());
  }

  deleteRow(rows: FormArray, rowIndex: number, parentGroups?: FormArray, groupIndex?: number): void {
    rows.removeAt(rowIndex);

    if (rows.length === 0 && parentGroups && groupIndex !== undefined) {
      parentGroups.removeAt(groupIndex);
    }

    this.updateGenerateButtonState();
  }

  private updateGenerateButtonState(): void {
    this.isButtonDisabled =
      this.segmentForm.invalid ||
      this.selectedDatasources.invalid ||
      this.selectedDatabases.invalid ||
      !this.hasConditionRows();
  }

  private hasConditionRows(): boolean {
    const groupHasRows = (group: FormGroup): boolean => {
      const rows = this.getRows(group);
      const childGroups = this.getGroups(group);
      return rows.length > 0 || childGroups.controls.some((childGroup) => groupHasRows(childGroup as FormGroup));
    };

    return this.singleRows.length > 0 || this.groups.controls.some((group) => groupHasRows(group as FormGroup));
  }

  showOperatorAfterRow(group: FormGroup, rowIndex: number): boolean {
    const rows = this.getRows(group);
    const groups = this.getGroups(group);

    // show operator if:
    // - there is another row OR
    // - there is at least one group after rows
    return rowIndex < rows.length - 1 || groups.length > 0;
  }

  getLastSingleRowGroup(): FormGroup | null {
    if (this.singleRows.length === 0) {
      return null;
    }
    return this.singleRows.at(this.singleRows.length - 1) as FormGroup;
  }

  private async loadMetadata(): Promise<void> {
    this.isMetadataLoading = true;
    this.metadataErrorMessage = '';
    this.dataSourceList = [];
    this.databaseList = [];
    this.tableList = [];
    this.tableMap.clear();
    this.dataSourceToDatabasesMap.clear();
    this.selectedDatasources.setValue([]);
    this.selectedDatabases.setValue([]);
    this.previousDatasourcesValue = [];

    try {
      const orgId = this.userManagementService.getOrgId();
      const metadataResponse = await this.getMetadata(orgId);
      const metadata = metadataResponse?.metadata ?? [];

      metadata.forEach((item) => this.addMetadataItem(item));

      this.dataSourceList = Array.from(new Set(metadata.map((item) => item.datasource_name?.trim() || '')))
        .filter(Boolean)
        .sort((left, right) => left.localeCompare(right));

      if (!this.dataSourceList.length) {
        this.metadataErrorMessage = this.noMetadataMessage;
      }
    } finally {
      this.isMetadataLoading = false;
    }
  }

  async getMetadata(source: string): Promise<GetMetadataQuery['GetMetadata'] | null> {
    return await this.fanExplorationService.getMetadata(source);
  }

  private addMetadataItem(item: Metadata): void {
    const datasourceName = item.datasource_name?.trim() || '';
    const databaseName = item.database_name?.trim() || '';
    const tableName = item.table_name?.trim() || '';

    if (!datasourceName || !databaseName || !tableName) {
      return;
    }

    this.addTableToMap(datasourceName, databaseName, {
      table_name: tableName,
      columns: item.columns ?? [],
    });
    this.addDatabaseToMap(datasourceName, databaseName);
  }

  private addTableToMap(
    dataSource: string,
    databaseName: string,
    item: { table_name: string; columns?: MetadataColumnsType[] | null },
  ): void {
    const key = `${dataSource}#${databaseName}`;
    const tableItem: DataBaseTableItem = {
      datasourceName: dataSource,
      databaseName,
      tableName: item.table_name,
      columns: item.columns ?? [],
    };
    const existingTableList = this.tableMap.get(key) ?? [];

    this.tableMap.set(key, [...existingTableList, tableItem]);
  }

  private addDatabaseToMap(dataSource: string, databaseName: string): void {
    const databaseList = this.dataSourceToDatabasesMap.get(dataSource) ?? [];

    if (!databaseList.includes(databaseName)) {
      this.dataSourceToDatabasesMap.set(dataSource, [...databaseList, databaseName]);
    }
  }

  private createConditionColumnValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const parent = control.parent as FormGroup | null;
      if (!parent) {
        return null;
      }

      const operator = String(parent.get('op')?.value ?? '');
      const columnType = this.getColumnType(String(control.value ?? ''));

      if (this.numericOperators.has(operator) && control.value && !this.isNumericColumnType(columnType)) {
        return { numericColumnRequired: true };
      }

      if (operator === 'like' && control.value && !this.isStringCompatibleColumnType(columnType)) {
        return { stringColumnRequired: true };
      }

      return null;
    };
  }

  private createConditionValueValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const parent = control.parent as FormGroup | null;
      if (!parent) {
        return null;
      }

      const rawValue = String(control.value ?? '').trim();
      if (!rawValue) {
        return null;
      }

      if (SQL_INJECTION_PATTERN.test(rawValue)) {
        return { sqlInjectionAttempt: true };
      }

      const operator = String(parent.get('op')?.value ?? '');
      const columnType = this.getColumnType(String(parent.get('column')?.value ?? ''));

      if (operator === 'like' && (this.isNumericLiteral(rawValue) || this.isBooleanLiteral(rawValue))) {
        return { likeStringOnly: true };
      }

      if (this.numericOperators.has(operator) && !this.isNumericLiteral(rawValue)) {
        return { numericValueRequired: true };
      }

      if (this.listOperators.has(operator)) {
        const tokens = rawValue.split(',').map((item) => this.stripWrappingQuotes(item.trim()));

        if (!tokens.length || tokens.some((item) => !item)) {
          return { listValuesRequired: true };
        }

        if (this.isNumericColumnType(columnType) && tokens.some((item) => !this.isNumericLiteral(item))) {
          return { listTypeMismatch: true };
        }

        if (this.isBooleanColumnType(columnType) && tokens.some((item) => !this.isBooleanLiteral(item))) {
          return { listTypeMismatch: true };
        }
      }

      return null;
    };
  }

  private bindConditionRowValidation(row: FormGroup): void {
    row.valueChanges.subscribe(() => {
      row.get('column')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      row.get('op')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      row.get('value')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    });
  }

  private refreshConditionValidity(): void {
    const refreshRows = (rows: FormArray): void => {
      for (const control of rows.controls as FormGroup[]) {
        control.get('column')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        control.get('op')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        control.get('value')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      }
    };

    const refreshGroups = (groups: FormArray): void => {
      for (const group of groups.controls as FormGroup[]) {
        refreshRows(this.getRows(group));
        refreshGroups(this.getGroups(group));
      }
    };

    refreshRows(this.singleRows);
    refreshGroups(this.groups);
  }

  private getColumnType(columnName: string): string {
    return this.columnList.find((column) => column.name === columnName)?.type?.toLowerCase() ?? '';
  }

  private isNumericColumnType(columnType: string): boolean {
    return /(int|float|double|decimal|numeric|number|bigint|smallint|tinyint|real)/i.test(columnType);
  }

  private isBooleanColumnType(columnType: string): boolean {
    return /(bool|boolean)/i.test(columnType);
  }

  private isStringCompatibleColumnType(columnType: string): boolean {
    if (!columnType) {
      return true;
    }

    return !this.isNumericColumnType(columnType) && !this.isBooleanColumnType(columnType);
  }

  private isNumericLiteral(value: string): boolean {
    return /^-?\d+(\.\d+)?$/.test(this.stripWrappingQuotes(value));
  }

  private isBooleanLiteral(value: string): boolean {
    return /^(true|false)$/i.test(this.stripWrappingQuotes(value));
  }

  private stripWrappingQuotes(value: string): string {
    return value.replace(/^['"]|['"]$/g, '');
  }

  getConditionColumnError(control: AbstractControl | null): string | null {
    if (!control?.errors) {
      return null;
    }

    if (control.errors['required']) {
      return 'Column selection is required.';
    }

    if (control.errors['numericColumnRequired']) {
      return 'This operator requires a numeric column.';
    }

    if (control.errors['stringColumnRequired']) {
      return 'Like operator only supports text columns.';
    }

    return null;
  }

  getConditionOperatorError(control: AbstractControl | null): string | null {
    if (!control?.errors) {
      return null;
    }

    if (control.errors['required']) {
      return 'Operator selection is required.';
    }

    return null;
  }

  getConditionValueError(control: AbstractControl | null): string | null {
    if (!control?.errors) {
      return null;
    }

    if (control.errors['required']) {
      return 'Value is required.';
    }

    if (control.errors['sqlInjectionAttempt']) {
      return 'Potential SQL injection content is not allowed.';
    }

    if (control.errors['likeStringOnly']) {
      return 'Like operator only accepts text values.';
    }

    if (control.errors['numericValueRequired']) {
      return 'This operator requires a numeric value.';
    }

    if (control.errors['listValuesRequired']) {
      return 'In/Not In requires comma-separated non-empty values.';
    }

    if (control.errors['listTypeMismatch']) {
      return 'One or more list values do not match the selected column type.';
    }

    return null;
  }

  private subscribeToMetadataSelectionChanges(): void {
    this.selectedDatasources.valueChanges.subscribe((datasources) => {
      const removedDatasources = this.previousDatasourcesValue.filter(
        (previousDatasource) => !datasources.includes(previousDatasource),
      );

      const removedDatabases = removedDatasources.flatMap(
        (removedDatasource) => this.dataSourceToDatabasesMap.get(removedDatasource) ?? [],
      );

      if (removedDatabases.length) {
        this.selectedDatabases.setValue(
          this.selectedDatabases.value.filter((database) => !removedDatabases.includes(database)),
        );
      }

      const result = new Set<string>();

      const sourceList = Array.isArray(datasources) ? datasources : datasources ? [datasources] : [];

      for (const datasource of sourceList) {
        const databases = this.dataSourceToDatabasesMap.get(datasource);

        if (databases?.length) {
          for (const db of databases) {
            result.add(db);
          }
        }
      }

      this.databaseList = Array.from(result).sort((a, b) => a.localeCompare(b));
      this.previousDatasourcesValue = sourceList;

      if (!sourceList.length || removedDatasources.length) {
        this.segmentForm.patchValue({ baseTable: null });
        this.tableList = [];
      }

      // Sync standalone control value back into the reactive form
      const datasourceValue = sourceList.length === 1 ? sourceList[0] : sourceList.join(',');
      this.segmentForm.patchValue({ dataSource: datasourceValue || '' });
      this.updateGenerateButtonState();
    });

    this.selectedDatabases.valueChanges.subscribe((databases) => {
      const result: DataBaseTableItem[] = [];

      const datasources = Array.isArray(this.selectedDatasources.value)
        ? this.selectedDatasources.value
        : this.selectedDatasources.value
          ? [this.selectedDatasources.value]
          : [];

      const databaseList = Array.isArray(databases) ? databases : databases ? [databases] : [];

      for (const datasource of datasources) {
        for (const database of databaseList) {
          const tables = this.tableMap.get(`${datasource}#${database}`);

          if (tables?.length) {
            result.push(...tables);
          }
        }
      }

      this.tableList = result.sort((a, b) => a.tableName.localeCompare(b.tableName));

      if (!databaseList.length) {
        this.segmentForm.patchValue({ baseTable: null });
        this.tableList = [];
      } else {
        const currentBaseTable = this.segmentForm.get('baseTable')?.value;
        if (currentBaseTable && !this.tableList.some((t) => t.tableName === currentBaseTable)) {
          this.segmentForm.patchValue({ baseTable: null });
        }
      }

      this.updateColumnsForSelectedTable();
      this.refreshConditionValidity();

      // Sync standalone control value back into the reactive form
      const dbValue = databaseList.length === 1 ? databaseList[0] : databaseList.join(',');
      this.segmentForm.patchValue({ database: dbValue || '' });
      this.updateGenerateButtonState();
    });

    this.segmentForm.get('baseTable')?.valueChanges.subscribe(() => {
      this.updateColumnsForSelectedTable();
      this.resetConditionsForTableChange();
      this.refreshConditionValidity();
      this.updateGenerateButtonState();
    });
  }

  private resetConditionsForTableChange(): void {
    this.singleRows.clear();
    this.groups.clear();
    this.segmentForm.get('operator')?.setValue('AND', { emitEvent: false });
    this.addSingleRow();
  }

  private updateColumnsForSelectedTable(): void {
    const selectedTable = this.segmentForm.get('baseTable')?.value;
    this.columnList = this.tableList.find((table) => table.tableName === selectedTable)?.columns ?? [];
  }

  private readonly operatorSqlMap: Record<string, string> = {
    equal_to: '=',
    not_equal_to: '!=',
    like: 'LIKE',
    less_than: '<',
    greater_than: '>',
    less_than_or_equal: '<=',
    greater_than_or_equal: '>=',
    in: 'IN',
    not_in: 'NOT IN',
  };

  private readonly sqlToFormOperatorMap: Record<string, string> = {
    '=': 'equal_to',
    '!=': 'not_equal_to',
    '<>': 'not_equal_to',
    LIKE: 'like',
    '<': 'less_than',
    '>': 'greater_than',
    '<=': 'less_than_or_equal',
    '>=': 'greater_than_or_equal',
    IN: 'in',
    'NOT IN': 'not_in',
  };

  private applyEditQueryRecord(): void {
    const record = this.editQueryRecord;
    if (!record || record.executionSource !== ExecutionSource.SEGMENT) {
      return;
    }

    this.queryId = record.queryId?.trim() || '';
    this.executionId = record.executionId?.trim() || '';
    this.generatedQueryText = record.query?.trim() || '';

    this.segmentForm.patchValue({
      segmentName: record.queryName?.trim() || '',
      segmentDesc: record.queryDescription?.trim() || '',
    });

    const selectedFilterConfig = this.parseSelectedFilterConfig(record.selectedFilterConfig);
    if (!selectedFilterConfig) {
      return;
    }

    this.applyTableSelectionFromConfig(selectedFilterConfig.tableName);
    this.applyConditionsFromConfig(selectedFilterConfig.conditions);
  }

  private parseSelectedFilterConfig(selectedFilterConfigRaw?: string | null): SegmentSelectedFilterConfig | null {
    const rawValue = selectedFilterConfigRaw?.trim();
    if (!rawValue) {
      return null;
    }

    try {
      const parsedConfig = JSON.parse(rawValue) as SegmentSelectedFilterConfig;
      if (!parsedConfig?.tableName || !Array.isArray(parsedConfig.conditions)) {
        return null;
      }

      return parsedConfig;
    } catch (error) {
      console.warn('Unable to parse selectedFilterConfig for segment edit prefill', error);
      return null;
    }
  }

  private applyTableSelectionFromConfig(tableNameWithDatabase: string): void {
    const [datasourceName = '', databaseName = '', baseTable = ''] = tableNameWithDatabase.split('.', 3);
    if (!datasourceName || !databaseName || !baseTable) {
      return;
    }

    this.segmentForm.patchValue({ dataSource: datasourceName, database: databaseName, baseTable });

    if (datasourceName) {
      this.selectedDatasources.setValue([datasourceName]);
    }

    this.selectedDatabases.setValue([databaseName]);
  }

  private applyConditionsFromConfig(conditionNodes: SegmentFilterNode[]): void {
    this.singleRows.clear();
    this.groups.clear();

    const rowNodes = conditionNodes.filter((node) => !!node.condition);
    const groupNodes = conditionNodes.filter((node) => !!node.group?.length);

    this.applyRootRows(rowNodes);
    this.applyRootGroups(groupNodes, rowNodes.length > 0);

    if (!this.singleRows.length && !this.groups.length) {
      this.addSingleRow();
    }
  }

  private applyRootRows(rowNodes: SegmentFilterNode[]): void {
    for (const rowNode of rowNodes) {
      const row = this.createRowFromConditionNode(rowNode);
      if (!row) {
        continue;
      }

      if (this.singleRows.length > 0) {
        const previousRow = this.singleRows.at(this.singleRows.length - 1) as FormGroup;
        previousRow.get('operator')?.setValue(this.normalizeJoinOperator(rowNode.operator));
      }

      this.singleRows.push(row);
    }
  }

  private applyRootGroups(groupNodes: SegmentFilterNode[], hasRootRows: boolean): void {
    for (const groupNode of groupNodes) {
      if (!groupNode.group?.length) {
        continue;
      }

      const group = this.createGroupFromNodes(groupNode.group);
      if (!group) {
        continue;
      }

      if (!this.groups.length && hasRootRows) {
        this.segmentForm.patchValue({
          operator: this.normalizeJoinOperator(groupNode.operator),
        });
      } else if (this.groups.length > 0) {
        const previousGroup = this.groups.at(this.groups.length - 1) as FormGroup;
        previousGroup.get('operator')?.setValue(this.normalizeJoinOperator(groupNode.operator));
      }

      this.groups.push(group);
    }
  }

  private createGroupFromNodes(nodes: SegmentFilterNode[]): FormGroup | null {
    const group = this.createGroup();
    const rows = this.getRows(group);
    const groups = this.getGroups(group);
    rows.clear();
    groups.clear();

    let previousType: 'row' | 'group' | null = null;
    let previousRow: FormGroup | null = null;
    let previousGroup: FormGroup | null = null;

    for (const node of nodes) {
      if (node.condition) {
        const row = this.createRowFromConditionNode(node);
        if (!row) {
          continue;
        }

        if (previousType === 'row' && previousRow) {
          previousRow.get('operator')?.setValue(this.normalizeJoinOperator(node.operator));
        }

        if (previousType === 'group' && previousGroup) {
          previousGroup.get('operator')?.setValue(this.normalizeJoinOperator(node.operator));
        }

        rows.push(row);
        previousType = 'row';
        previousRow = row;
        previousGroup = null;
        continue;
      }

      if (node.group?.length) {
        const childGroup = this.createGroupFromNodes(node.group);
        if (!childGroup) {
          continue;
        }

        if (previousType === 'row' && previousRow) {
          previousRow.get('operator')?.setValue(this.normalizeJoinOperator(node.operator));
        }

        if (previousType === 'group' && previousGroup) {
          previousGroup.get('operator')?.setValue(this.normalizeJoinOperator(node.operator));
        }

        groups.push(childGroup);
        previousType = 'group';
        previousGroup = childGroup;
        previousRow = null;
      }
    }

    return rows.length || groups.length ? group : null;
  }

  private createRowFromConditionNode(node: SegmentFilterNode): FormGroup | null {
    const condition = node.condition;
    if (!condition?.colName || !condition.operator) {
      return null;
    }

    const row = this.createRow();
    row.patchValue({
      column: condition.colName,
      op: this.toFormOperator(condition.operator),
      value: condition.value,
      operator: 'AND',
    });

    return row;
  }

  private toFormOperator(operator: string): string {
    const normalizedOperator = operator.trim().toUpperCase();
    return this.sqlToFormOperatorMap[normalizedOperator] || this.sqlToFormOperatorMap[operator] || 'equal_to';
  }

  private normalizeJoinOperator(operator: string | undefined): 'AND' | 'OR' {
    return String(operator).toUpperCase() === '1' ? 'OR' : 'AND';
  }

  private buildConditionClause(column: string, op: string, value: string): string {
    const sqlOp = this.operatorSqlMap[op] ?? op;
    if (sqlOp === 'IN' || sqlOp === 'NOT IN') {
      return `${column} ${sqlOp} (${value})`;
    }
    if (sqlOp === 'LIKE') {
      return `${column} ${sqlOp} '${value}'`;
    }
    const isNumeric = /^-?\d+(\.\d+)?$/.test(value.trim());
    return isNumeric ? `${column} ${sqlOp} ${value}` : `${column} ${sqlOp} '${value}'`;
  }

  private buildRowsClause(rows: FormArray): string {
    const parts: string[] = [];
    const controls = rows.controls as FormGroup[];

    for (let i = 0; i < controls.length; i++) {
      const { column, op, value, operator } = controls[i].value;
      if (!column || !op || value === '' || value === null || value === undefined) continue;
      parts.push(this.buildConditionClause(column, op, value));
      if (i < controls.length - 1) {
        parts.push(String(operator).toUpperCase() === '1' ? 'OR' : 'AND');
      }
    }
    return parts.join(' ');
  }

  private buildGroupClause(group: AbstractControl): string {
    const rows = this.getRows(group) as FormArray;
    const childGroups = this.getGroups(group) as FormArray;

    const rowsClause = this.buildRowsClause(rows);
    const childClauses: string[] = [];

    for (let ci = 0; ci < childGroups.controls.length; ci++) {
      const childClause = this.buildGroupClause(childGroups.controls[ci]);
      if (childClause) {
        childClauses.push(childClause);
        if (ci < childGroups.controls.length - 1) {
          childClauses.push(
            String((childGroups.controls[ci] as FormGroup).value.operator).toUpperCase() === '1' ? 'OR' : 'AND',
          );
        }
      }
    }

    const parts: string[] = [];
    if (rowsClause) parts.push(rowsClause);
    if (childClauses.length) {
      if (parts.length) {
        const lastRowGroup = rows.controls[rows.controls.length - 1] as FormGroup | undefined;
        parts.push(String(lastRowGroup?.value.operator).toUpperCase() === '1' ? 'OR' : 'AND');
      }
      parts.push(...childClauses);
    }

    return parts.length ? `(${parts.join(' ')})` : '';
  }

  private mapToSymbolOperator(operator: string): string {
    return this.operatorSqlMap[operator] ?? operator;
  }

  private buildRowConditionNode(rowControl: FormGroup, operator: string): SegmentFilterNode | null {
    const { column, op, value } = rowControl.value;
    if (!column || !op || value === '' || value === null || value === undefined) {
      return null;
    }

    return {
      operator,
      condition: {
        colName: String(column),
        operator: this.mapToSymbolOperator(String(op)),
        value: String(value).trim(),
      },
    };
  }

  private buildRowNodes(rows: FormArray): SegmentFilterNode[] {
    const nodes: SegmentFilterNode[] = [];
    const controls = rows.controls as FormGroup[];
    let nextNodeOperator = '';

    for (const control of controls) {
      const node = this.buildRowConditionNode(control, nextNodeOperator);

      if (node) {
        nodes.push(node);
      }

      nextNodeOperator = String(control.value.operator).toUpperCase() === '1' ? 'OR' : 'AND';
    }

    return nodes;
  }

  private buildGroupChildren(group: AbstractControl): SegmentFilterNode[] {
    const groupForm = group as FormGroup;
    const rows = this.getRows(groupForm);
    const childGroups = this.getGroups(groupForm);
    const rowNodes = this.buildRowNodes(rows);
    const childGroupNodes = this.buildGroupNodes(childGroups);

    if (rowNodes.length && childGroupNodes.length) {
      const rowsToGroupsOperator =
        String((rows.controls[rows.controls.length - 1] as FormGroup)?.value?.operator).toUpperCase() === 'OR'
          ? 'OR'
          : 'AND';
      childGroupNodes[0] = {
        ...childGroupNodes[0],
        operator: rowsToGroupsOperator,
      };
    }

    return [...rowNodes, ...childGroupNodes];
  }

  private buildGroupNodes(groups: FormArray): SegmentFilterNode[] {
    const nodes: SegmentFilterNode[] = [];
    const controls = groups.controls as FormGroup[];
    let nextNodeOperator = '';

    for (const control of controls) {
      const children = this.buildGroupChildren(control);
      if (children.length) {
        nodes.push({
          operator: nextNodeOperator,
          group: children,
        });
      }

      nextNodeOperator = String(control.value.operator).toUpperCase() === '1' ? 'OR' : 'AND';
    }

    return nodes;
  }

  private buildSelectedFilterConfig(): SegmentSelectedFilterConfig | null {
    const baseTable: string = this.segmentForm.get('baseTable')?.value ?? '';
    const datasource: string = this.segmentForm.get('dataSource')?.value ?? '';
    const databases = Array.isArray(this.selectedDatabases.value)
      ? this.selectedDatabases.value
      : this.selectedDatabases.value
        ? [this.selectedDatabases.value]
        : [];
    const database = databases[0] ?? '';

    if (!database || !baseTable) {
      return null;
    }

    const rowNodes = this.buildRowNodes(this.singleRows);
    const groupNodes = this.buildGroupNodes(this.groups);

    if (rowNodes.length && groupNodes.length) {
      const rootJoinOperator = String(this.segmentForm.get('operator')?.value).toUpperCase() === '1' ? 'OR' : 'AND';
      groupNodes[0] = {
        ...groupNodes[0],
        operator: rootJoinOperator,
      };
    }

    return {
      tableName: `${datasource}.${database}.${baseTable}`,
      conditions: [...rowNodes, ...groupNodes],
    };
  }

  buildQuery(): string {
    const baseTable: string = this.segmentForm.get('baseTable')?.value ?? '';
    const datasource: string = this.segmentForm.get('dataSource')?.value ?? '';
    const databases = Array.isArray(this.selectedDatabases.value)
      ? this.selectedDatabases.value
      : this.selectedDatabases.value
        ? [this.selectedDatabases.value]
        : [];
    const database = databases[0] ?? '';

    if (!database || !baseTable) return '';

    const singleRowsClause = this.buildRowsClause(this.singleRows);

    const groupClauses: string[] = [];
    for (let gi = 0; gi < this.groups.controls.length; gi++) {
      const groupClause = this.buildGroupClause(this.groups.controls[gi]);
      if (groupClause) {
        groupClauses.push(groupClause);
        if (gi < this.groups.controls.length - 1) {
          groupClauses.push(
            String((this.groups.controls[gi] as FormGroup).value.operator).toUpperCase() === '1' ? 'OR' : 'AND',
          );
        }
      }
    }
    const groupsClause = groupClauses.join(' ');

    let whereClause = '';
    if (singleRowsClause && groupsClause) {
      const bridgeOperator = String(this.segmentForm.get('operator')?.value).toUpperCase() === '1' ? 'OR' : 'AND';
      whereClause = `${singleRowsClause} ${bridgeOperator} ${groupsClause}`;
    } else {
      whereClause = singleRowsClause || groupsClause;
    }

    const from = `${datasource}.${database}.${baseTable}`;
    return whereClause ? `SELECT COUNT(*) FROM ${from} WHERE ${whereClause}` : `SELECT COUNT(*) FROM ${from}`;
  }

  private async saveDraftAndSetIds(): Promise<boolean> {
    const { segmentName, segmentDesc } = this.segmentForm.value;
    const query = this.buildQuery();
    const selectedFilterConfig = this.buildSelectedFilterConfig();

    if (!query || !selectedFilterConfig || !selectedFilterConfig.conditions.length) {
      this.messageService.warning('Unable to build query. Please verify your selections.');
      return false;
    }

    const queryRecordInput = {
      name: segmentName.trim(),
      description: segmentDesc?.trim() || '',
      executionSource: ExecutionSource.SEGMENT,
      executionId: this.executionId || undefined,
      query,
      queryId: this.queryId || undefined,
      queryType: QueryType.SEGMENT,
      selectedFilterConfig: JSON.stringify(selectedFilterConfig),
    };

    this.isSavingDraft = true;
    try {
      const response = await this.fanExplorationService.saveDraft(queryRecordInput);

      if (response?.queryId && response?.executionId) {
        this.queryId = response.queryId;
        this.executionId = response.executionId;
        this.generatedQueryText = query;
        this.messageService.success('Segment saved successfully.');
        return true;
      }

      this.messageService.error('Unable to save segment.');
      return false;
    } catch (error) {
      console.error('Error saving segment', error);
      this.messageService.error('Failed to save segment. Please try again.');
      return false;
    } finally {
      this.isSavingDraft = false;
    }
  }

  async onGenerateDataClick(): Promise<void> {
    if (this.segmentForm.invalid || this.selectedDatasources.invalid || this.selectedDatabases.invalid) {
      this.segmentForm.markAllAsTouched();
      this.messageService.warning('Please complete required fields before generating data.');
      return;
    }

    this.isSubmittingQuery = true;

    try {
      const draftSaved = await this.saveDraftAndSetIds();
      if (!draftSaved) {
        this.messageService.error('Unable to save segment before generating data.');
        return;
      }

      const response = await this.fanExplorationService.submitQuery({
        queryId: this.queryId,
        executionId: this.executionId,
      });

      if (response?.queryId && response?.executionId) {
        this.queryId = response.queryId;
        this.executionId = response.executionId;
        this.messageService.success('Segment query submitted successfully.');
        this.historyTabRequested.emit();
        return;
      }

      this.messageService.error('Unable to submit segment query.');
    } catch (error) {
      console.error('Error submitting segment query', error);
      this.messageService.error('Failed to submit segment query. Please try again.');
    } finally {
      this.isSubmittingQuery = false;
    }
  }
}
